import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Mail, Phone, Shield, HardHat, Edit2, Trash2, Key, Building2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

// User Details Modal
const UserDetailsModal = ({ open, onClose, userId, onUpdate }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (open && userId) {
      fetchUserDetails();
    }
  }, [open, userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/users/${userId}`);
      setUser(res.data);
      setEditData({
        name: res.data.name,
        phone: res.data.phone || '',
        role: res.data.role,
        engineer_type: res.data.engineer_type || '',
        status: res.data.status || 'active'
      });
    } catch (error) {
      toast.error('Failed to fetch user details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${userId}`, editData);
      toast.success('User updated successfully');
      setEditing(false);
      fetchUserDetails();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.post(`/users/${userId}/reset-password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setShowPasswordReset(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      onUpdate?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Admin', className: 'bg-purple-100 text-purple-700' },
      customer: { label: 'Customer', className: 'bg-blue-100 text-blue-700' },
      engineer: { label: 'Engineer', className: 'bg-orange-100 text-orange-700' }
    };
    return badges[role] || { label: role, className: 'bg-stone-100 text-stone-700' };
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white border-0 shadow-2xl rounded-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">
            User Details
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            View and manage user information
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto"></div>
            <p className="text-stone-500 mt-2">Loading...</p>
          </div>
        ) : user && (
          <div className="space-y-4 pt-2">
            {/* User Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
                  ) : (
                    <User className="h-6 w-6 text-stone-500" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">{user.name}</h3>
                  <Badge className={getRoleBadge(user.role).className}>
                    {getRoleBadge(user.role).label}
                    {user.engineer_type && ` (${user.engineer_type})`}
                  </Badge>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(!editing)}
                >
                  {editing ? <X className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {editing ? (
              // Edit Form
              <div className="space-y-4 bg-stone-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="border-stone-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    className="border-stone-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={editData.role}
                    onValueChange={(value) => setEditData({ ...editData, role: value })}
                  >
                    <SelectTrigger className="border-stone-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="engineer">Engineer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editData.role === 'engineer' && (
                  <div className="space-y-2">
                    <Label>Engineer Type</Label>
                    <Select
                      value={editData.engineer_type}
                      onValueChange={(value) => setEditData({ ...editData, engineer_type: value })}
                    >
                      <SelectTrigger className="border-stone-200">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="design">Design Engineer</SelectItem>
                        <SelectItem value="construction">Construction Engineer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex space-x-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditing(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <Mail className="h-4 w-4 mr-3 text-stone-400" />
                  <span className="text-stone-600">{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center text-sm">
                    <Phone className="h-4 w-4 mr-3 text-stone-400" />
                    <span className="text-stone-600">{user.phone}</span>
                  </div>
                )}
                <div className="flex items-center text-sm">
                  <Building2 className="h-4 w-4 mr-3 text-stone-400" />
                  <span className="text-stone-600">{user.project_count || 0} Projects</span>
                </div>
                <div className="flex items-center text-sm">
                  <Shield className="h-4 w-4 mr-3 text-stone-400" />
                  <span className="text-stone-600">
                    Joined {new Date(user.created_at).toLocaleDateString('en-IN')}
                  </span>
                </div>
              </div>
            )}

            {/* Password Reset Section */}
            {showPasswordReset ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                <Label className="text-amber-800">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="border-amber-200"
                />
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowPasswordReset(false);
                      setNewPassword('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handlePasswordReset}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Reset Password
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex space-x-2 pt-4 border-t border-stone-100">
                <Button
                  variant="outline"
                  onClick={() => setShowPasswordReset(true)}
                  className="flex-1"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Reset Password
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// User List Component
const UserManagement = ({ users, onRefresh }) => {
  const [selectedUserId, setSelectedUserId] = useState(null);

  const getRoleBadge = (role, engineerType) => {
    const badges = {
      admin: { label: 'Admin', className: 'bg-purple-100 text-purple-700' },
      customer: { label: 'Customer', className: 'bg-blue-100 text-blue-700' },
      engineer: { label: engineerType === 'design' ? 'Design Eng.' : 'Construction Eng.', className: 'bg-orange-100 text-orange-700' }
    };
    return badges[role] || { label: role, className: 'bg-stone-100 text-stone-700' };
  };

  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => {
          const roleBadge = getRoleBadge(user.role, user.engineer_type);
          return (
            <Card 
              key={user.user_id} 
              className="border-stone-200 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedUserId(user.user_id)}
              data-testid={`user-card-${user.user_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <User className="h-5 w-5 text-stone-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-stone-900 truncate">{user.name}</h4>
                      <Badge className={`${roleBadge.className} text-xs`}>
                        {roleBadge.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-500 truncate">{user.email}</p>
                    {user.phone && (
                      <p className="text-xs text-stone-400">{user.phone}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <UserDetailsModal
        open={!!selectedUserId}
        onClose={() => setSelectedUserId(null)}
        userId={selectedUserId}
        onUpdate={onRefresh}
      />
    </>
  );
};

export default UserManagement;
export { UserDetailsModal };
